import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
  alpha,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CategoryIcon from '@mui/icons-material/Category';
import API from '../../api/axios';

const CATEGORY_COLOR_MAP = {
  Domestic: { color: '#3b82f6' },
  International: { color: '#10b981' },
  Religious: { color: '#f59e0b' },
  Wildlife: { color: '#22c55e' },
};

const getCategoryColor = (name) =>
  CATEGORY_COLOR_MAP[name]?.color || '#8b5cf6';

const ManageCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({ name: '', is_active: true });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/categories');
      setCategories(Array.isArray(data) ? data : []);
    } catch {
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (cat = null) => {
    if (cat) {
      setEditingCategory(cat);
      setFormData({ name: cat.name, is_active: Boolean(cat.is_active) });
    } else {
      setEditingCategory(null);
      setFormData({ name: '', is_active: true });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
    setFormErrors({});
  };

  const validate = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Category name is required';
    if (formData.name.trim().length > 50) errors.name = 'Name must be 50 characters or less';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { name: formData.name.trim(), is_active: formData.is_active ? 1 : 0 };
      if (editingCategory) {
        await API.put(`/categories/${editingCategory.id}`, payload);
        setSnackbar({ open: true, message: 'Category updated successfully', severity: 'success' });
      } else {
        await API.post('/categories', payload);
        setSnackbar({ open: true, message: 'Category created successfully', severity: 'success' });
      }
      fetchCategories();
      handleCloseDialog();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save category. Please try again.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (id) => { setDeleteId(id); setDeleteDialogOpen(true); };
  const closeDeleteDialog = () => { setDeleteId(null); setDeleteDialogOpen(false); };

  const handleDelete = async () => {
    try {
      await API.delete(`/categories/${deleteId}`);
      setSnackbar({ open: true, message: 'Category deleted successfully', severity: 'success' });
      fetchCategories();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to delete category';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      closeDeleteDialog();
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, color: 'text.primary' }}>
            Manage Categories
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Add, edit or remove tour categories — including Wildlife and custom types
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            borderRadius: 2.5,
            px: 3,
            py: 1,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 15px rgba(102,126,234,0.35)',
            '&:hover': { boxShadow: '0 8px 25px rgba(102,126,234,0.5)' },
          }}
        >
          Add Category
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {/* Info banner */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          border: '1px solid rgba(102,126,234,0.2)',
          borderRadius: 2,
          bgcolor: alpha('#667eea', 0.04),
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <CategoryIcon sx={{ color: '#667eea', fontSize: 22, flexShrink: 0 }} />
        <Typography variant="body2" color="text.secondary">
          Categories defined here appear across the entire site — in the Tours page tabs, Inquiry Form,
          Booking Form, Offers, and Admin panels. Any new category you add (like "Wildlife") will
          automatically appear everywhere without needing code changes.
        </Typography>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fc' }}>
                {['Category Name', 'Status', 'Tours', 'Actions'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', py: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                    No categories found. Add your first category!
                  </TableCell>
                </TableRow>
              ) : categories.map((cat) => {
                const color = getCategoryColor(cat.name);
                return (
                  <TableRow key={cat.id} sx={{ '&:hover': { bgcolor: alpha('#667eea', 0.02) }, transition: 'background-color 0.2s' }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" fontWeight={700}>{cat.name}</Typography>
                        {!cat.is_active && (
                          <Chip label="Hidden" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: alpha('#6b7280', 0.1), color: '#6b7280' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={cat.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          color: cat.is_active ? '#10b981' : '#6b7280',
                          bgcolor: cat.is_active ? alpha('#10b981', 0.1) : alpha('#6b7280', 0.1),
                          border: `1px solid ${cat.is_active ? alpha('#10b981', 0.2) : alpha('#6b7280', 0.2)}`,
                          borderRadius: 1.5,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit Category">
                          <IconButton size="small" onClick={() => handleOpenDialog(cat)} sx={{ color: '#3b82f6', bgcolor: alpha('#3b82f6', 0.1), '&:hover': { bgcolor: alpha('#3b82f6', 0.2) } }}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Category">
                          <IconButton size="small" onClick={() => openDeleteDialog(cat.id)} sx={{ color: '#ef4444', bgcolor: alpha('#ef4444', 0.1), '&:hover': { bgcolor: alpha('#ef4444', 0.2) } }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 800, pb: 2 }}>
          {editingCategory ? 'Edit Category' : 'Add New Category'}
          <IconButton onClick={handleCloseDialog} size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: alpha('#ef4444', 0.1) } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderBottom: 'none', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth
              label="Category Name *"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="e.g. Wildlife, Adventure, Honeymoon..."
              error={!!formErrors.name}
              helperText={formErrors.name || 'This name will appear as a tab throughout the site'}
              inputProps={{ maxLength: 50 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={700} color="text.primary">Active (visible on site)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Inactive categories are hidden from all public-facing pages.
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 3, gap: 1.5, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
          <Button onClick={handleCloseDialog} variant="outlined" sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600, color: 'text.secondary', borderColor: 'grey.300' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving}
            sx={{ borderRadius: 2, px: 4, textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', boxShadow: '0 4px 12px rgba(102,126,234,0.3)', '&:hover': { boxShadow: '0 6px 18px rgba(102,126,234,0.45)' } }}
          >
            {saving ? <CircularProgress size={24} sx={{ color: 'white' }} /> : (editingCategory ? 'Update Category' : 'Save Category')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} PaperProps={{ sx: { borderRadius: 3, maxWidth: 400, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#ef4444' }}>Delete Category?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary">
            This will permanently delete the category. You cannot delete a category that has tours or offers assigned to it.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={closeDeleteDialog} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" onClick={handleDelete} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, boxShadow: '0 4px 12px rgba(239,68,68,0.3)' }}>
            Yes, Delete it
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert variant="filled" severity={snackbar.severity} sx={{ borderRadius: 2, fontWeight: 500 }} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageCategories;
